import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { ArrowLeft, CalendarCheck, Check, ChevronDown, ShoppingCart, SlidersHorizontal, Ticket } from 'lucide-react';
import eventSettingsApi from 'api/requests/eventSettings';
import Checkbox from 'components/_UI/Checkbox';
import Loader from 'components/_UI/Loader';
import { useEvent } from 'context/events/Event';
import { EVENT_LAYOUTS, eventDays } from 'helpers/eventSessions';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

export const DEFAULT_SETTINGS = {
    checkin_mode: 'single',
    allow_ticket_transfer: true,
    presence_unmark_until: 'session_start',
    one_order_per_user: false,
};

const CHECKIN_MODES = [
    {
        value: 'single',
        title: 'Uma vez',
        description: 'O ingresso entra uma única vez, como sempre foi.',
    },
    {
        value: 'per_day',
        title: 'Uma vez por dia',
        description: 'O mesmo ingresso entra uma vez em cada dia do evento. A madrugada conta como o dia anterior.',
    },
];

const UNMARK_OPTIONS = [
    { value: 'session_start', label: 'Até o início da sessão' },
    { value: 'one_hour', label: 'Até 1 hora antes da sessão' },
    { value: 'one_day', label: 'Até 1 dia antes da sessão' },
    { value: 'never', label: 'Não permitir desmarcar' },
];

/**
 * Configurações avançadas do evento. Cada mudança vale na hora (como a escolha de
 * layout): nada de botão de salvar para esquecer de apertar.
 */
export default function AdvancedSettingsSection({ data, loading, onBack, toast, onSuccess }) {
    const { event } = useEvent();
    const [values, setValues] = useState({ ...DEFAULT_SETTINGS, ...(data || {}) });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setValues({ ...DEFAULT_SETTINGS, ...(data || {}) });
    }, [data]);

    const isSchedule = event?.layout === EVENT_LAYOUTS.SCHEDULE;
    const oneDay = eventDays(event).length <= 1;

    async function change(patch) {
        if (saving) {
            return;
        }

        const previous = values;

        setValues({ ...values, ...patch });
        setSaving(true);

        try {
            const { data: response } = await eventSettingsApi.update(event.slug, patch);

            setValues({ ...DEFAULT_SETTINGS, ...response?.data });
            onSuccess(response?.data);
            toast.success('Configuração salva.', { position: 'top-center' });
        } catch (error) {
            setValues(previous);
            toast.error('Não foi possível salvar a configuração.', { position: 'top-center' });
        } finally {
            setSaving(false);
        }
    }

    return (
        <Wrapper>
            <BackRow
                onClick={onBack}
                role='button'
                tabIndex={0}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onBack()}
            >
                <BackIconWrap>
                    <ArrowLeft size={18} color={panelAccent(colors.secondaryPurple)} />
                </BackIconWrap>
                <BackLabel>Voltar</BackLabel>
            </BackRow>

            {loading ? (
                <LoaderWrap><Loader /></LoaderWrap>
            ) : (
                <>
                    <SectionCard>
                        <CardHeader
                            icon={SlidersHorizontal}
                            title='Check-in da entrada'
                            description='Como o ingresso é validado na porta do evento. Vale para ingressos; produtos (retirada) são sempre de uso único.'
                        />

                        <Divider />

                        <ChoiceGrid role='radiogroup' aria-label='Check-in da entrada'>
                            {CHECKIN_MODES.map(item => {
                                const selected = values.checkin_mode === item.value;

                                return (
                                    <ChoiceCard
                                        key={item.value}
                                        type='button'
                                        role='radio'
                                        aria-checked={selected}
                                        $selected={selected}
                                        disabled={saving}
                                        onClick={() => !selected && change({ checkin_mode: item.value })}
                                    >
                                        <ChoiceTitle>
                                            {item.title}
                                            {selected && <Check size={16} strokeWidth={3} />}
                                        </ChoiceTitle>
                                        <ChoiceText>{item.description}</ChoiceText>
                                    </ChoiceCard>
                                );
                            })}
                        </ChoiceGrid>

                        {values.checkin_mode === 'per_day' && oneDay && (
                            <Note>
                                Este evento tem um dia só, então as duas opções funcionam igual. A escolha passa a
                                valer se você adicionar mais dias.
                            </Note>
                        )}
                    </SectionCard>

                    <SectionCard $spaced>
                        <CardHeader
                            icon={Ticket}
                            title='Ingressos'
                            description='Regras do que o comprador pode fazer com o ingresso depois da compra.'
                        />

                        <Divider />

                        <Checkbox
                            label='Permitir que o comprador transfira o ingresso para outra pessoa'
                            selected={!!values.allow_ticket_transfer}
                            onSelect={() => change({ allow_ticket_transfer: !values.allow_ticket_transfer })}
                            marginTop='0px'
                        />
                    </SectionCard>

                    <SectionCard $spaced>
                        <CardHeader
                            icon={ShoppingCart}
                            title='Compras'
                            description='Regras para quem compra pela página do evento.'
                        />

                        <Divider />

                        <Checkbox
                            label='Permitir só uma compra por pessoa'
                            selected={!!values.one_order_per_user}
                            onSelect={() => change({ one_order_per_user: !values.one_order_per_user })}
                            marginTop='0px'
                        />
                        <Note>
                            Cada conta faz um pedido só neste evento, com os ingressos que quiser dentro do limite de
                            cada um. Quem sair do pagamento sem pagar pode refazer o carrinho. Para limitar quantas
                            unidades cabem na compra, use "Limite por compra" no cadastro do ingresso.
                        </Note>
                    </SectionCard>

                    {isSchedule && (
                        <SectionCard $spaced>
                            <CardHeader
                                icon={CalendarCheck}
                                title='Presença nas sessões'
                                description='Vale para o layout de programação.'
                            />

                            <Divider />

                            <FieldLabel htmlFor='unmark-until'>Desmarcar presença</FieldLabel>
                            <SelectBox>
                                <Select
                                    id='unmark-until'
                                    value={values.presence_unmark_until}
                                    disabled={saving}
                                    onChange={e => change({ presence_unmark_until: e.target.value })}
                                >
                                    {UNMARK_OPTIONS.map(item => (
                                        <option key={item.value} value={item.value}>{item.label}</option>
                                    ))}
                                </Select>
                                <ChevronDown size={18} color={colors.night} aria-hidden='true' />
                            </SelectBox>
                            <Note>Depois do prazo, a pessoa continua com a presença marcada e não consegue desmarcar.</Note>
                        </SectionCard>
                    )}
                </>
            )}
        </Wrapper>
    );
}

/** Cabeçalho de cada bloco de configurações: ícone, título e uma linha de explicação. */
function CardHeader({ icon: Icon, title, description }) {
    return (
        <HeaderLeft>
            <IconBadge>
                <Icon size={20} color={panelAccent(colors.occasionalPurple)} />
            </IconBadge>
            <HeaderText>
                <SectionTitle>{title}</SectionTitle>
                <SectionDescription>{description}</SectionDescription>
            </HeaderText>
        </HeaderLeft>
    );
}

const slideIn = keyframes`
    from { opacity: 0; transform: translateX(16px); }
    to { opacity: 1; transform: translateX(0); }
`;

const Wrapper = styled.div`
    animation: ${slideIn} 0.25s ease-out;
`;

const BackRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 6px 12px 6px 6px;
    margin-bottom: 16px;
    width: fit-content;
    border-radius: 10px;
    outline: none;
    transition: background 0.15s ease;

    &:hover { background: ${colors.neutralDark}; }
    &:focus-visible { box-shadow: 0 0 0 2px ${panelAccentAlpha(colors.secondaryPurple, '44')}; }
`;

const BackIconWrap = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BackLabel = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 500;
    color: ${colors.nightDark};
`;

const SectionCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 24px;
    border: 1px solid ${colors.night}11;
    box-shadow: 0 1px 8px ${colors.night}08;
    margin-top: ${({ $spaced }) => ($spaced ? '16px' : 0)};
    text-align: left;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 14px;
`;

const IconBadge = styled.div`
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 10px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '12')};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const SectionTitle = styled.span`
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const SectionDescription = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    line-height: 1.5;
`;

const Divider = styled.div`
    align-self: stretch;
    height: 1px;
    background: ${colors.night}11;
    margin: 20px 0;
`;

const ChoiceGrid = styled.div`
    align-self: stretch;
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const ChoiceCard = styled.button`
    display: flex;
    flex-direction: column;
    gap: 6px;
    text-align: left;
    padding: 16px;
    border-radius: 12px;
    cursor: pointer;
    font-family: inherit;
    background: ${({ $selected }) => ($selected ? panelAccentAlpha(colors.occasionalPurple, '0D') : colors.neutralLight)};
    border: 2px solid ${({ $selected }) => ($selected ? panelAccent(colors.occasionalPurple) : `${colors.night}22`)};

    &:disabled { opacity: 0.6; cursor: wait; }
    &:focus-visible { box-shadow: 0 0 0 2px ${panelAccentAlpha(colors.secondaryPurple, '44')}; }
`;

const ChoiceTitle = styled.span`
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: ${fonts.sizes.highlight};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ChoiceText = styled.span`
    font-size: ${fonts.sizes.small};
    line-height: 1.5;
    color: ${colors.night};
`;

const Note = styled.p`
    margin: 14px 0 0;
    font-size: ${fonts.sizes.small};
    line-height: 1.5;
    color: ${colors.night};
`;

const LoaderWrap = styled.div`
    display: flex;
    justify-content: center;
    padding: 32px 0;
`;

const FieldLabel = styled.label`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const SelectBox = styled.div`
    position: relative;
    width: 100%;
    max-width: 360px;
    margin-top: 6px;

    svg {
        position: absolute;
        top: 50%;
        right: 16px;
        transform: translateY(-50%);
        pointer-events: none;
    }
`;

const Select = styled.select`
    display: block;
    width: 100%;
    padding: 11px 48px 11px 14px;
    border-radius: 8px;
    border: 1.5px solid #D4D4D4;
    background: ${colors.neutralLight};
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    font-family: inherit;
    font-size: ${fonts.sizes.default};
    color: ${colors.nightDark};
    cursor: pointer;
    outline: transparent;
    transition: all ease .25s;

    &:hover { border-color: ${colors.nightLight}; }
    &:disabled { opacity: 0.6; cursor: wait; }

    &:focus {
        border-color: ${panelAccent(colors.occasionalPurple)};
        box-shadow: 0 0 0 3px ${panelAccentAlpha(colors.occasionalPurple, '1A')};
    }
`;
